import React, { createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import {
	setInstitutionId,
	setAccessToken,
	clearAuth,
} from '../store/authSlice';
import { setUserData, clearUserData } from '../store/userSlice';
import { paths } from '../routes';
import { persistor } from '../store';

const AuthContext = createContext();

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { institutionId, accessToken, isAuthenticated } = useSelector(
		(state) => state.auth,
	);
	const userData = useSelector((state) => state.user);

	const updateAuthState = useCallback(
		(institutionId, token) => {
			dispatch(setInstitutionId(institutionId));
			dispatch(setAccessToken(token));
			apiService.setAuthToken(token);
		},
		[dispatch],
	);

	const fetchInstitutionData = useCallback(async (institutionId) => {
		try {
			// For dashboard requests, use JWT with internal request header
			const response = await apiService.get(`/institution/${institutionId}`, {
				headers: {
					'x-internal-request': 'true'
				}
			});
			if (response.data.status === 200) {
				const { institution } = response.data.data;
				// Update the user data in Redux store
				dispatch(setUserData(institution));
				console.log('📊 Institution data fetched and stored:', institution);
			}
		} catch (error) {
			console.error('Failed to fetch institution data:', error);
			toast.error('Failed to fetch institution data.');
		}
	}, [dispatch]);

	const getUserData = useCallback(
		async (institutionId) => {
			try {
				const response = await apiService.get('/institution');
				if (response.data.status === 200) {
					const data = response.data.data;
					dispatch(setUserData(data));
					return data;
				}
			} catch (error) {
				throw error;
			}
		},
		[dispatch],
	);

	const loginInstitution = useCallback(
		async (clientId, clientSecret) => {
			try {
				const response = await apiService.post('/institution/login', {
					clientId,
					clientSecret,
				});
				console.log(response);

				if (response.data.status === 200) {
					const {
						id,
						token,
						keys,
						currentEnvironment,
						isVerified,
						verificationStatus,
						...restData
					} = response.data.data;

					// Update auth state
					updateAuthState(id, token);

					// Determine current API key based on environment preference (default to test)
					const environment = currentEnvironment || 'test';
					const currentApiKey =
						environment === 'live' && keys?.production?.activated
							? keys.production.apiKey
							: keys?.test?.apiKey;

					// Store auth data in localStorage for persistence
					localStorage.setItem('kaino_current_api_key', currentApiKey || '');
					localStorage.setItem('kaino_institution_id', id.toString());
					localStorage.setItem('kaino_current_environment', environment);

					// Set user data in Redux
					const completeUserData = {
						id,
						keys,
						dashboardEnvironment: environment,
						currentApiKey,
						isVerified,
						verificationStatus,
						...restData,
					};

					dispatch(setUserData(completeUserData));

					toast.success('Login successful!');

					// Fetch institution data after login
					await fetchInstitutionData(id);

					return response.data;
				} else {
					throw new Error(response.data.message || 'Login failed');
				}
			} catch (error) {
				throw error;
			}
		},
		[updateAuthState, dispatch, fetchInstitutionData],
	);

	const logoutUser = useCallback(() => {
		dispatch(clearAuth());
		dispatch(clearUserData());
		apiService.setAuthToken(null);
		persistor.purge();
		navigate(paths.LOGIN);
		toast.success('Logged out successfully');
	}, [dispatch, navigate]);

	const value = {
		// Auth state
		isAuthenticated,
		institutionId,
		accessToken,
		userData,
		// Actions
		loginInstitution,
		logoutUser,
		fetchInstitutionData,
		getUserData,
	};

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
};
